function hasRatingScore(source) {
  if (!source || typeof source !== "object") return false;
  const score = Number(source.score);
  return Number.isFinite(score) && score > 0;
}

/** Normalize discover list score_breakdown or public API source_scores for eligibility checks. */
export function scoreBreakdownFromApiItem(item) {
  const bd = item?.scoreBreakdown || item?.score_breakdown;
  if (bd && typeof bd === "object") return bd;
  const src = item?.source_scores;
  if (!src || typeof src !== "object") return undefined;
  return {
    letterboxd: { score: src.letterboxd?.score ?? null },
    imdb: { score: src.imdb?.score ?? null },
    metacritic: { score: src.metacritic?.score ?? null },
    rottentomatoes: { score: src.rottentomatoes?.score ?? null },
  };
}

export function ratingsFromItem(item) {
  if (item?.ratings && typeof item.ratings === "object") return item.ratings;
  const bd = scoreBreakdownFromApiItem(item);
  if (!bd) return {};
  return {
    letterboxd: bd.letterboxd,
    imdb: bd.imdb,
    metacritic: bd.metacritic,
    rottentomatoes: bd.rottentomatoes,
  };
}

export function isMovieMediaType(item) {
  const mt = String(item?.mediaType || item?.media_type || "movie").toLowerCase();
  return mt !== "tv" && mt !== "tv-show" && mt !== "tv_show" && mt !== "series" && mt !== "person";
}

export function movieMeetsCuratorScoreRequirements(ratings = {}) {
  // Letterboxd is the primary user signal — sufficient on its own for display.
  if (hasRatingScore(ratings.letterboxd)) return true;
  const hasUser = hasRatingScore(ratings.imdb);
  const hasCritic = hasRatingScore(ratings.metacritic) || hasRatingScore(ratings.rottentomatoes);
  return hasUser && hasCritic;
}

/** Movies need Letterboxd or IMDb before any score line or curator sort key applies. */
export function movieHasUserRatingSources(item) {
  if (!item) return false;
  const ratings = ratingsFromItem(item);
  return hasRatingScore(ratings.letterboxd) || hasRatingScore(ratings.imdb);
}

const MIN_CURATOR_SCORE_RATINGS = 10000;

function hasEnoughRatingsForScore(item, minimum = MIN_CURATOR_SCORE_RATINGS) {
  const count = Number(item?.ratingsCount ?? item?.curator_metrics?.total_ratings ?? item?.vote_count ?? 0);
  return Number.isFinite(count) && count >= minimum;
}

function hasCatalogCuratorPercentile(item) {
  const percentile = Number(
    item?.curator_metrics?.score_percentile
    ?? item?.curator_score_percentile
    ?? item?.scoreBreakdown?.curator?.percentile
    ?? item?.scoreBreakdown?.curator?.score_percentile
  );
  return item?.inCuratorCatalog !== false && Number.isFinite(percentile) && percentile > 0;
}

/** True when a movie may display a Curator score (TV always eligible if score exists). */
export function movieHasDisplayableCuratorScore(item) {
  if (!item) return false;
  if (!isMovieMediaType(item)) return true;

  const ratings = ratingsFromItem(item);
  const breakdownHasSources = ["letterboxd", "imdb", "metacritic", "rottentomatoes"]
    .some((key) => hasRatingScore(ratings[key]));
  const hasUser = movieHasUserRatingSources(item);

  // When source breakdown is present, block RT/MC-only rows (e.g. The Inspection).
  if (breakdownHasSources && !hasUser) return false;

  if (hasCatalogCuratorPercentile(item) && hasEnoughRatingsForScore(item)) {
    if (!isMovieMediaType(item)) return true;
    if (breakdownHasSources && !hasUser) return false;
    if (breakdownHasSources) return movieMeetsCuratorScoreRequirements(ratings);
    return true;
  }

  if (!hasUser) return false;
  if (!breakdownHasSources) return false;
  return movieMeetsCuratorScoreRequirements(ratings);
}
